import React, { useRef, useCallback } from 'react'

const LEVELS = [
  {
    value: 1,
    label: 'Completely unclear',
    subtitle: "I don't know what matters most",
    iconKey: 'fragmented',
    ringColor: '#E24B4A',
    bgTint: '#FCEBEB',
    accentColor: '#E24B4A',
    titleColor: '#791F1F',
    subtitleColor: '#A32D2D',
  },
  {
    value: 2,
    label: 'Mostly unclear',
    subtitle: "I'm pulled in too many directions",
    iconKey: 'loose',
    ringColor: '#D85A30',
    bgTint: '#FAECE7',
    accentColor: '#D85A30',
    titleColor: '#712B13',
    subtitleColor: '#8E3A17',
  },
  {
    value: 3,
    label: 'Finding focus',
    subtitle: 'The big picture is resolving',
    iconKey: 'half_resolved',
    ringColor: '#BA7517',
    bgTint: '#FAEEDA',
    accentColor: '#BA7517',
    titleColor: '#412402',
    subtitleColor: '#633806',
  },
  {
    value: 4,
    label: 'Mostly clear',
    subtitle: 'I know what matters, with some noise',
    iconKey: 'solid',
    ringColor: '#639922',
    bgTint: '#EAF3DE',
    accentColor: '#639922',
    titleColor: '#173404',
    subtitleColor: '#27500A',
  },
  {
    value: 5,
    label: 'Crystal clear',
    subtitle: 'I know exactly what matters',
    iconKey: 'bullseye',
    ringColor: '#1D9E75',
    bgTint: '#E1F5EE',
    accentColor: '#1D9E75',
    titleColor: '#04342C',
    subtitleColor: '#0F6E56',
  },
]

export { LEVELS as CLARITY_LEVELS }

function FragmentedIcon({ selected, style }) {
  const strokeW = selected ? 1.8 : 1.3
  const color = '#E24B4A'
  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="28" cy="22" r="16" fill="none" stroke={color} strokeWidth={strokeW} strokeDasharray="2 3" />
      <circle cx="28" cy="22" r="10" fill="none" stroke={color} strokeWidth={strokeW} strokeDasharray="2 3" />
      <circle cx="28" cy="22" r="4" fill="none" stroke={color} strokeWidth={strokeW} strokeDasharray="2 3" />
    </svg>
  )
}

function LooseIcon({ selected, style }) {
  const strokeW = selected ? 1.9 : 1.4
  const color = '#D85A30'
  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="28" cy="22" r="16" fill="none" stroke={color} strokeWidth={strokeW} strokeDasharray="4 3" />
      <circle cx="28" cy="22" r="10" fill="none" stroke={color} strokeWidth={strokeW} strokeDasharray="4 3" />
      <circle cx="28" cy="22" r="4" fill={color} opacity="0.5" />
    </svg>
  )
}

function HalfResolvedIcon({ selected, style }) {
  const outerStrokeW = selected ? 2.0 : 1.5
  const innerStrokeW = selected ? 2.2 : 1.8
  const color = '#BA7517'
  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="28" cy="22" r="16" fill="none" stroke={color} strokeWidth={outerStrokeW} strokeDasharray="8 3" />
      <circle cx="28" cy="22" r="10" fill="none" stroke={color} strokeWidth={innerStrokeW} />
      <circle cx="28" cy="22" r="4" fill={color} />
    </svg>
  )
}

function SolidIcon({ selected, style }) {
  const outerStrokeW = selected ? 2.0 : 1.6
  const innerStrokeW = selected ? 2.3 : 1.9
  const color = '#639922'
  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="28" cy="22" r="16" fill="none" stroke={color} strokeWidth={outerStrokeW} />
      <circle cx="28" cy="22" r="10" fill="none" stroke={color} strokeWidth={innerStrokeW} />
      <circle cx="28" cy="22" r="4" fill={color} />
    </svg>
  )
}

function BullseyeIcon({ selected, style }) {
  const outerStrokeW = selected ? 2.4 : 1.8
  const innerStrokeW = selected ? 2.6 : 2.0
  const color = '#1D9E75'
  return (
    <svg viewBox="0 0 56 44" xmlns="http://www.w3.org/2000/svg" style={style}>
      <circle cx="28" cy="22" r="16" fill="none" stroke={color} strokeWidth={outerStrokeW} />
      <circle cx="28" cy="22" r="10" fill="none" stroke={color} strokeWidth={innerStrokeW} />
      <circle cx="28" cy="22" r="4" fill={color} />
    </svg>
  )
}

function IconFor({ iconKey, selected, style }) {
  const props = { selected, style }
  switch (iconKey) {
    case 'fragmented':    return <FragmentedIcon {...props} />
    case 'loose':         return <LooseIcon {...props} />
    case 'half_resolved': return <HalfResolvedIcon {...props} />
    case 'solid':         return <SolidIcon {...props} />
    case 'bullseye':      return <BullseyeIcon {...props} />
    default:              return null
  }
}

export function ClarityRingsScale({ value, onChange, disabled }) {
  const buttonsRef = useRef([])
  const selected = value?.value ?? null
  const selectedLevel = selected ? LEVELS.find((l) => l.value === selected) : null

  const handleClick = useCallback((level) => {
    if (disabled) return
    onChange({ value: level.value, label: level.label })
  }, [disabled, onChange])

  const handleKeyDown = useCallback((e, index) => {
    if (disabled) return
    let nextIndex = null
    if (e.key === 'ArrowRight') {
      nextIndex = Math.min(index + 1, LEVELS.length - 1)
    } else if (e.key === 'ArrowLeft') {
      nextIndex = Math.max(index - 1, 0)
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = LEVELS.length - 1
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick(LEVELS[index])
      return
    } else {
      return
    }
    e.preventDefault()
    buttonsRef.current[nextIndex]?.focus()
  }, [disabled, handleClick])

  const focusableIndex = selected ? LEVELS.findIndex((l) => l.value === selected) : 0

  return (
    <div style={styles.container}>
      {/* Icon row */}
      <div style={styles.iconRow}>
        {LEVELS.map((level, index) => {
          const isSelected = selected === level.value
          const opacity = disabled ? 0.4 : isSelected ? 1 : 0.55
          return (
            <button
              key={level.value}
              ref={(el) => { buttonsRef.current[index] = el }}
              style={{
                ...styles.iconButton,
                opacity,
                cursor: disabled ? 'default' : 'pointer',
                transition: 'opacity var(--duration-fast) ease',
              }}
              onClick={() => handleClick(level)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onMouseEnter={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.8'
              }}
              onMouseLeave={(e) => {
                if (!disabled && !isSelected) e.currentTarget.style.opacity = '0.55'
              }}
              disabled={disabled}
              tabIndex={index === focusableIndex ? 0 : -1}
              aria-label={`Clarity level ${level.value}: ${level.label}`}
              aria-pressed={isSelected}
            >
              <IconFor
                iconKey={level.iconKey}
                selected={isSelected}
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
            </button>
          )
        })}
      </div>

      {/* Verdict panel */}
      {selectedLevel && (
        <div
          style={{
            ...styles.verdictPanel,
            background: selectedLevel.bgTint,
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ ...styles.accentBar, background: selectedLevel.accentColor }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: selectedLevel.titleColor }}>
              {selectedLevel.label}
            </div>
            <div style={{ fontSize: 11, color: selectedLevel.subtitleColor }}>
              {selectedLevel.subtitle}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 100,
    padding: '12px 14px',
  },
  iconRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'clamp(4px, 1.5vw, 12px)',
    padding: '0 clamp(2px, 1vw, 8px)',
    width: '100%',
  },
  iconButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    flex: '1 1 0',
    minWidth: 32,
    maxWidth: '20%',
    aspectRatio: '56 / 44',
  },
  verdictPanel: {
    marginTop: 14,
    padding: '10px 14px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  accentBar: {
    width: 6,
    height: 24,
    borderRadius: 3,
    flexShrink: 0,
  },
}
